import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { finalize, switchMap, tap } from 'rxjs';
import { UsersService } from 'core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { getUserAvatarUrl } from 'shared/utils/user-avatar.utils';
import { AuthService } from 'core/services/auth.service';
import { User } from 'shared/types/user.model';
import { IIdValue } from 'shared/types/id-value.interface';
import { IState } from 'shared/types/state.interface';
import { CountriesService } from 'core';
import { IAvatar } from 'shared/types/avatar.interface';
import { UpdateEmailModalComponent } from '../update-email-modal/update-email-modal.component';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { KorviuScrollbarComponent } from 'shared/components/korviu-scrollbar/korviu-scrollbar.component';
import { KorviuInputComponent } from 'shared/components/korviu-input/korviu-input.component';
import { CountrySelectComponent } from 'shared/components/country-select/country-select.component';
import { KorviuSelectComponent } from 'shared/components/korviu-select/korviu-select.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { Account } from 'shared/types/payment-account.interface';
import { PaymentAccountsService } from 'core/services/payment-accounts.service';
import { UserAvatarV2Component } from 'shared/components/user-avatar-v2/user-avatar-v2.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

const LevelNames = {
  1: 'my-forest.muirWoods',
  2: 'my-forest.greatSmokyMountins',
  3: 'my-forest.cherokee',
  4: 'my-forest.shawnee',
  5: 'my-forest.ocala',
  6: 'my-forest.tongass'
};

interface AvatarOption extends IAvatar {
  imageUrl: string;
  treeType: number;
}

@Component({
  selector: 'app-user-info-edit',
  templateUrl: './user-info-edit.component.html',
  styleUrl: './user-info-edit.component.scss',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    KorviuLoaderComponent,
    NzAvatarModule,
    KorviuScrollbarComponent,
    KorviuInputComponent,
    CountrySelectComponent,
    KorviuSelectComponent,
    NzSpinModule,
    KorviuButtonComponent,
    FormsModule,
    UserAvatarV2Component
  ]
})
export class UserInfoEditComponent implements OnInit {
  #router = inject(Router);
  #destroyRef = inject(DestroyRef);

  public readonly avatars = signal<AvatarOption[]>([]);
  public readonly selectedAvatarId = signal<number | null>(null);
  public readonly selectedAvatar = computed(() => this.avatars().find(avatar => avatar.id === this.selectedAvatarId()));

  public user: User;
  public personalInfoIsLoading: boolean;
  public updateInfoForm: FormGroup;
  public states: IIdValue[];
  public statesIsLoading: boolean;
  public avatarsIsLoading: boolean;

  public get isSpecial(): boolean {
    return this.user?.isSpecial;
  }

  public get emailHasBeenChanged(): boolean {
    return this.emailControl?.value !== this.user?.email;
  }

  public get levelName(): string {
    return LevelNames[this.user?.level?.id];
  }

  public get firstNameControl(): AbstractControl {
    return this.updateInfoForm?.get('first_name');
  }

  public get lastNameControl(): AbstractControl {
    return this.updateInfoForm?.get('last_name');
  }

  public get emailControl(): AbstractControl {
    return this.updateInfoForm?.get('email');
  }

  public get phoneNumberCountryIdControl(): AbstractControl {
    return this.updateInfoForm?.get('phone_number_country_id');
  }

  public get phoneNumberControl(): AbstractControl {
    return this.updateInfoForm?.get('phone_number');
  }

  public get countryControl(): AbstractControl {
    return this.updateInfoForm?.get('country_id');
  }

  public get stateControl(): AbstractControl {
    return this.updateInfoForm?.get('state_id');
  }

  public get cityControl(): AbstractControl {
    return this.updateInfoForm?.get('city');
  }

  public get zipControl(): AbstractControl {
    return this.updateInfoForm?.get('zip_code');
  }

  public get streetControl(): AbstractControl {
    return this.updateInfoForm?.get('address');
  }

  public get contractHolderNameControl(): AbstractControl {
    return this.updateInfoForm?.get('organization_name');
  }

  public get contactPersonControl(): AbstractControl {
    return this.updateInfoForm?.get('contact_person');
  }

  constructor(
    private _usersService: UsersService,
    private _authService: AuthService,
    private _modalService: NzModalService,
    private _countriesService: CountriesService,
    private _paymentAccountsService: PaymentAccountsService
  ) {
    this.avatarsIsLoading = false;
    this.user = null;
    this.personalInfoIsLoading = false;
    this.updateInfoForm = new FormGroup({
      first_name: new FormControl(''),
      last_name: new FormControl(''),
      country_id: new FormControl(''),
      state_id: new FormControl(''),
      city: new FormControl(''),
      zip_code: new FormControl(''),
      phone_number: new FormControl(''),
      phone_number_country_id: new FormControl(''),
      email: new FormControl(''),
      organization_name: new FormControl(''),
      contact_person: new FormControl('')
    });

    this.countryControl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value: number) => {
      this._loadStatesByCountryId(+value);
    });
  }

  private _setDefaultValidators(): void {
    this.updateInfoForm.get('first_name').setValidators([Validators.required]);
    this.updateInfoForm.get('last_name').setValidators([Validators.required]);
    this.updateInfoForm.get('country_id').setValidators([Validators.required]);
    this.updateInfoForm.get('state_id').setValidators([Validators.required]);
    this.updateInfoForm.get('city').setValidators([Validators.required, Validators.minLength(2)]);
    this.updateInfoForm.get('zip_code').setValidators([Validators.required]);
    this.updateInfoForm
      .get('phone_number')
      .setValidators([Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(10)]);
    this.updateInfoForm.get('phone_number_country_id').setValidators([Validators.required]);
    this.updateInfoForm.get('email').setValidators([Validators.required, Validators.email]);

    if (this.isSpecial) {
      this.updateInfoForm.get('contact_person').setValidators([Validators.required]);
    }
  }

  public ngOnInit(): void {
    this._loadPersonalInfo();
    this._loadAvatarsList();
  }

  public onSelectAvatar(id: number, disabled: boolean): void {
    if (disabled) {
      return;
    }
    this.selectedAvatarId.set(id);
  }

  public isAvatarDisabled(avatar: IAvatar): boolean {
    if (this.isSpecial) {
      return false;
    }
    return avatar.level > (this.user?.level?.id || 1);
  }

  public controlHasValue(control: AbstractControl): boolean {
    return typeof control.value === 'number' || control.value;
  }

  public onUpdateInfoCancelClick(): void {
    this._cancelEditProfile();
  }

  public onUpdateInfoSaveClick(): void {
    this._setDefaultValidators();
    for (let key in this.updateInfoForm.controls) {
      this.updateInfoForm.controls[key].updateValueAndValidity({ emitEvent: false });
      this.updateInfoForm.controls[key].markAsDirty();
      this.updateInfoForm.controls[key].setValidators([]);
      this.updateInfoForm.controls[key].setAsyncValidators([]);
    }
    if (this.updateInfoForm.valid) {
      if (this.emailHasBeenChanged) {
        this._modalService
          .create({
            nzContent: UpdateEmailModalComponent,
            nzWidth: 554,
            nzCentered: true,
            nzWrapClassName: 'modal-wrap',
            nzData: { ...this._getUpdateInfoFormValueForUpdate(), avatar_id: this.selectedAvatarId() }
          })
          .afterClose.pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe((result: boolean) => {
            if (typeof result === 'boolean' && result) {
              this._loadPersonalInfo(true, true);
            }
          });
      } else {
        this._updatePersonalInfo();
      }
    }
  }

  private _getUpdateInfoFormValueForUpdate(): any {
    const phoneNumberCountryPrefix = this.updateInfoForm.value.phone_number_country_id;
    const phoneNumberIdsByPrefixes = {
      '+52': 145,
      '+1': 240
    };
    const phoneNumberCountryId = phoneNumberIdsByPrefixes[phoneNumberCountryPrefix];

    return { ...this.updateInfoForm.value, phone_number_country_id: phoneNumberCountryId };
  }

  private _cancelEditProfile(): void {
    this.#router.navigate(['/workspace/profile']);
  }

  private _updatePersonalInfo(): void {
    this.personalInfoIsLoading = true;
    this._usersService
      .changePersonalInfo({ ...this._getUpdateInfoFormValueForUpdate(), avatar_id: this.selectedAvatarId() })
      .pipe(
        finalize(() => {
          this.personalInfoIsLoading = false;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this._loadPersonalInfo(true, true);
      });
  }

  private _loadStatesByCountryId(countryId: number): void {
    this.statesIsLoading = true;
    this._countriesService
      .getStatesByCountryId(countryId)
      .pipe(
        finalize(() => {
          this.statesIsLoading = false;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((states: IState[]) => {
        this.states = states.map((state: IState) => {
          return { id: state.id, value: state.name };
        });
      });
  }

  private _loadPersonalInfo(reload?: boolean, redirectToProfile?: boolean): void {
    this.personalInfoIsLoading = true;
    this._authService
      .getMe(reload)
      .pipe(
        tap((user: User) => {
          this.user = user;
          this._fillUpdateForm(user);
          this.selectedAvatarId.set(user?.avatar?.id ?? null);
        }),
        switchMap(() => this._paymentAccountsService.getUserPaymentAccounts()),
        tap((accounts: Account[]) => {
          if (redirectToProfile) {
            this.#router.navigate(['/workspace/profile']);
          }
        }),
        finalize(() => (this.personalInfoIsLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  private _fillUpdateForm(user: User): void {
    const phonePrefix = user?.phoneNumberCountryCode || '';
    const fullPhoneNumber = user?.fullPhoneNumber || '';

    this.updateInfoForm.patchValue({
      first_name: user?.userObj?.first_name || '',
      last_name: user?.userObj?.last_name || '',
      organization_name: user?.fullName || '',
      contract_terms: user?.userObj?.contract_terms || '',
      contact_person: user?.contactName || '',
      ganasocios: user?.userPartner?.full_name || '',
      ganaliados: user?.userRecruiter?.full_name || '',
      ganagentes: user?.userCordinator?.full_name || '',
      city: user?.userObj?.city || '',
      country_id: user?.userObj?.country?.id || '',
      state_id: user?.userObj?.state?.id || '',
      zip_code: user?.userObj?.zip_code || '',
      phone_number:
        phonePrefix && fullPhoneNumber.startsWith(phonePrefix)
          ? fullPhoneNumber.slice(phonePrefix.length)
          : fullPhoneNumber,
      phone_number_country_id: phonePrefix,
      email: user?.userObj?.email || '',
      address: user?.userObj?.address || ''
    });
  }

  private _loadAvatarsList(): void {
    this.avatarsIsLoading = true;
    this._usersService
      .getAvatarsList()
      .pipe(
        finalize(() => {
          this.avatarsIsLoading = false;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((avatarsResponse: IAvatar[]) => {
        let avatars: IAvatar[] = [];
        for (let i = 1; i < 11; i++) {
          avatars = avatars.concat(
            avatarsResponse
              .filter((avatar: IAvatar) => avatar?.image.includes(`P${i}_`))
              .sort((a: IAvatar, b: IAvatar) => a.level - b.level)
          );
        }
        this.avatars.set(
          avatars.map(avatar => ({
            ...avatar,
            imageUrl: getUserAvatarUrl(avatar.image),
            treeType: Number(avatar.image.match(/^P(\d+)_/)?.[1])
          }))
        );
      });
  }
}
